import { effect } from "@preact/signals";
import * as preact from "preact";
import * as _ from "underscore";

import * as analytics from "analytics";
import * as deckSelection from "deckSelection";
import * as deviceDetect from "deviceDetect";
import * as editCardDialog from "editCardDialog";
import * as haptics from "haptics";
import * as keyboardShortcuts from "keyboardShortcuts";
import * as localAPI from "localAPI";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";
import { SoundEffectView } from "soundEffect";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import * as tts from "tts";
import * as urlUtils from "urlUtils";
import ActiveMultipleChoiceChallengeView from "views/ActiveMultipleChoiceChallengeView";
import DefaultChallengeView from "views/DefaultChallengeView";
import DropdownMenu from "views/DropdownMenu";
import IntroChallengeView from "views/IntroChallengeView";
import MakeSentenceChallengeView from "views/MakeSentenceChallengeView";
import PassiveMultipleChoiceChallengeView from "views/PassiveMultipleChoiceChallengeView";
import PracticeEditView from "views/PracticeEditView";
import SessionHeaderView from "views/SessionHeaderView";
import * as speakingModeControls from "views/speakingModeControls";
import * as ttsMenu from "views/ttsMenu";
import TypingChallengeView from "views/TypingChallengeView";
import * as walkthrough from "walkthrough";

type Props = {
  appRouter: any;
  options?: {
    bookID?: string;
    source?: string;
    userWordIDs?: string[];
  };
};

interface Card {
  wordID: string;
  stage: "intro" | "passive" | "active" | "skipped";
  shownInSession: boolean;
}

interface State {
  status: "loading" | "challengeActive" | "challengeComplete";
  feedback?: {
    type: "correct" | "incorrect" | "almost" | "skipped" | "deleted";
    message?: {
      title: string;
      body?: string;
    };
    showLeniencySettings?: boolean;
    showTypingModeToggle?: boolean;
  };
  queue: Card[];
  completed: Card[];
  checkEnabled: boolean;

  // Number of challenges completed so far (including repeats of the same word)
  challengeCount: number;

  initialProgressScore: number;

  editingCard: boolean;
  edited: boolean;

  // Snapshot of the challenge widget's internal state captured when the user
  // clicks EDIT, restored when they return so the widget shows the answer
  // they left rather than resetting to a fresh interactive state.
  challengePersistedState?: unknown;

  showLeniencySettings: boolean;

  selectedContextIndex?: number;

  challengesCompleted: number;
  challengesCorrect: number;
  challengeShowingExplanation: boolean;

  introCardMode?: "active" | "passive" | "delete";

  showingMenu: boolean;
  message?: string;
  showingNote: boolean;
}

class PracticePageView extends preact.Component<Props, State> {
  startTime: number;
  cardsPerSession: number;
  editView: PracticeEditView;
  challengeView: any;
  scrollableArea: HTMLElement;
  from?: string;
  disposeUserEffect?: () => void;
  disposeTtsAvailabilityEffect?: () => void;

  constructor(props: Props) {
    super(props);

    this.cardsPerSession = practiceUtils.cardsPerSession(stores.user);

    this.state = {
      status: "loading",
      queue: [],
      completed: [],
      challengeCount: 0,
      initialProgressScore: 0,

      editingCard: false,
      edited: false,
      showLeniencySettings: false,
      checkEnabled: false,

      challengesCorrect: 0,
      challengesCompleted: 0,

      introCardMode: undefined,
      challengeShowingExplanation: false,

      showingMenu: false,
      showingNote: false,
    };
    this.init();
    if (stores.user.state.value.readerTranslationFont === "normal") {
      document.body.classList.add("normalTranslationFont");
    }
    this.from = urlUtils.getUrlVar("from");
  }

  async init() {
    if (await localAPI.isLocalMode()) {
      // Not signed in, redirect to home page
      window.location.href = "/";
    }

    this.startTime = Date.now();

    let readyToTest;
    let specificWordsReadyToTest: UserWordStore[] = [];

    if (this.props.options?.bookID) {
      readyToTest = await stores.practiceStore.fetchBookWordsToTest([
        this.props.options.bookID,
      ]);
    } else if (this.props.options?.source) {
      readyToTest = await stores.practiceStore.fetchSourceWordsToTest(
        this.props.options.source
      );
    } else if (this.props.options?.userWordIDs) {
      readyToTest = (
        await stores.practiceStore.fetchGroups(["reviewedToday", "readyToTest"])
      ).readyToTest;
      for (const userWordID of this.props.options.userWordIDs) {
        const userWord = stores.practiceStore.get(userWordID);
        if (userWord) {
          specificWordsReadyToTest.push(userWord);
        } else {
          // Fetch it
          const word = new UserWordStore({ _id: userWordID });
          try {
            await word.fetch();
          } catch (error) {
            console.error("ERROR fetching word: ", userWordID);
            // Don't add this word to queue
            continue;
          }
          if (!word.hasField("word")) {
            throw new Error("no word exists with that id");
          } else {
            specificWordsReadyToTest.push(word);
            stores.practiceStore.add(word);
          }
        }
      }
    } else {
      readyToTest = (
        await stores.practiceStore.fetchGroups(["reviewedToday", "readyToTest"])
      ).readyToTest;
    }

    if (
      specificWordsReadyToTest.length > 0 &&
      specificWordsReadyToTest[0]?.state.value.language !==
        stores.user.currentLearningLanguage.value
    ) {
      stores.user.save({
        currentLearningLanguage:
          specificWordsReadyToTest[0]?.state.value.language,
      });
    }

    // Prepare the queue. Pin explicitly-selected words first, then (unless this
    // is a chat session, which only practices the specific words) fill the rest
    // from the server-ordered ready words.
    const selectedWords =
      this.from === "chat"
        ? specificWordsReadyToTest
        : deckSelection.selectDeckWords({
            candidates: readyToTest,
            specificWords: specificWordsReadyToTest,
            sessionSize: this.cardsPerSession,
          });

    const queue = selectedWords.map((userWord) => ({
      wordID: userWord.id,
      stage:
        this.from === "chat"
          ? "active"
          : !userWord.hasField("lastRecallEase")
            ? ("intro" as const)
            : this.practiceType() === "blitz" ||
                userWord.state.value.lastRecallEase < 1 ||
                Number(userWord.state.value.interval) <
                  7 * spacedRepetition.DAY ||
                userWord.state.value.passiveMode
              ? ("passive" as const)
              : ("active" as const),
      shownInSession: false,
    }));

    analytics.trackEventWithProperties("startFlashcards", {
      numberOfCards: queue.length,
      from: this.props.options?.bookID ? "specificText" : "all",
      "Practice Type": this.practiceType(),
    });

    await Promise.all(
      queue.map((card) => stores.practiceStore.get(card.wordID).fetch())
    );

    if (queue.length === 0) {
      // No words to practice, redirect to learn page
      alert("Sorry, but there are no words ready to practice right now.");

      this.props.appRouter.navigate("/learn", {
        trigger: true,
      });
      return;
    }

    this.setState({
      status: "challengeActive",
      queue,
      initialProgressScore: this.progressScore(queue),
      selectedContextIndex:
        queue.length > 0
          ? this.pickContextIndex(stores.practiceStore.get(queue[0].wordID))
          : undefined,

      introCardMode: this.getIntroCardMode(queue[0]?.wordID),
    });

    for (const key of [
      keyboardShortcuts.keyCodes.enter,
      keyboardShortcuts.keyCodes.space,
    ]) {
      keyboardShortcuts.registerHandler("practicePage", key, (event) => {
        if (this.state.status === "challengeComplete") {
          event.stopPropagation();
          event.preventDefault();
          this.nextCard();
        }
      });
    }

    keyboardShortcuts.registerHandler(
      "practicePage",
      "E".charCodeAt(0),
      () => {
        if (this.state.editingCard) {
          this.stopEditingCard();
          return;
        }
        this.setState({ editingCard: !this.state.editingCard });
      },
      true,
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );

    keyboardShortcuts.registerHandler(
      "practicePage",
      "D".charCodeAt(0),
      () => {
        this.deleteCard();
      },
      true,
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );

    keyboardShortcuts.registerHandler(
      "practicePage",
      "F".charCodeAt(0),
      async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const currentCard = this.state.queue[0];
        const userWord = stores.practiceStore.get(currentCard.wordID);

        await userWord.save(
          { favorite: !userWord.state.value.favorite },
          { patch: true }
        );

        const message = `${userWord.isPhrase() ? "Phrase" : "Word"} ${userWord.state.value.favorite ? "Favorited ★" : "Unfavorited ☆"}`;

        if (this.state.message) {
          // Dismiss existing message first
          await this.setState({
            message: undefined,
          });
          await utils.sleep(300);
        }
        await this.setState({
          message,
        });

        await utils.sleep(4000);

        // Dismiss the message if it's still the same
        if (this.state.message === message) {
          this.setState({ message: undefined });
        }
      },
      true,
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
  }

  getIntroCardMode(userWordID) {
    return stores.practiceStore.get(userWordID)?.state.value.passiveMode
      ? "passive"
      : stores.user.flashcardMode.value === "passive"
        ? "passive"
        : "active";
  }

  progressScore(cards: Card[]) {
    return cards.reduce((score, card) => {
      switch (card.stage) {
        case "intro":
          return score + 0;

        case "passive":
          return score + 1;

        case "active":
          return score + 2;
      }
    }, 0);
  }

  progress() {
    const completedScore = 3;
    const maxProgressScore =
      (this.state.queue.length + this.state.completed.length) * completedScore;

    const progressScore =
      this.progressScore([...this.state.queue]) +
      this.state.completed.length * completedScore;

    return (
      (progressScore - this.state.initialProgressScore) /
      (maxProgressScore - this.state.initialProgressScore)
    );
  }

  lastTTSChallengeIndex = -1;

  autoPlayTTSIfAppropriate(challengeType) {
    if (!stores.user.speakingMode.value) {
      return;
    }

    if (this.lastTTSChallengeIndex === this.state.challengeCount) {
      // Already done this challenge
      return;
    }

    this.lastTTSChallengeIndex = this.state.challengeCount;

    switch (challengeType) {
      case "intro":
      case "passiveMultipleChoice": {
        // Play the word
        const userWord = stores.practiceStore.get(this.state.queue[0].wordID);
        practiceUtils.playTTS(
          userWord.state.value.language,
          userWord.state.value.word
        );
        break;
      }

      default:
      // Do nothing
    }
  }

  renderChallenge(card: Card) {
    const challengeType: practiceUtils.ChallengeType = (() => {
      switch (card.stage) {
        case "intro":
          return "intro";

        case "passive":
          // Pick passive challenge type
          return practiceUtils.passiveChallengeTypes[
            this.state.challengeCount %
              practiceUtils.passiveChallengeTypes.length
          ];

        case "active":
          // Pick active challenge type
          return practiceUtils.activeChallengeTypes[
            this.state.challengeCount %
              practiceUtils.activeChallengeTypes.length
          ];
      }
    })();

    this.autoPlayTTSIfAppropriate(challengeType);

    const ChallengeView = (() => {
      switch (challengeType) {
        case "intro":
          return IntroChallengeView;

        // Passive challenges
        case "passiveMultipleChoice":
          return PassiveMultipleChoiceChallengeView;

        case "pickWord":
          return null;

        case "arrange":
          return null;

        case "match":
          return null;

        // Passive+
        case "makeSentence":
          return MakeSentenceChallengeView;

        case "activeMultipleChoice":
          return ActiveMultipleChoiceChallengeView;

        // Active challenges
        case "typing":
          return TypingChallengeView;

        default:
          return DefaultChallengeView;
      }
    })();

    return (
      <div>
        <ChallengeView
          key={this.state.challengeCount}
          ref={(ref) => {
            this.challengeView = ref;
          }}
          userWord={stores.practiceStore.get(card.wordID)}
          completedCallback={(nextStep: practiceUtils.NextStep) => {
            switch (nextStep.type) {
              case "continue":
                this.nextCard();
                break;

              case "allow-continue":
                if (this.state.status !== "challengeComplete") {
                  this.setState({
                    status: "challengeComplete",
                    feedback: undefined,
                    checkEnabled: false,
                  });
                }
                break;

              case "show-feedback":
                haptics.play(
                  nextStep.correct === "yes"
                    ? "success"
                    : nextStep.correct === "no"
                      ? "error"
                      : "warning"
                );
                this.setState({
                  status: "challengeComplete",
                  feedback: {
                    type:
                      nextStep.correct === "yes"
                        ? "correct"
                        : nextStep.correct === "no"
                          ? "incorrect"
                          : "almost",
                    message: nextStep.message,
                    showLeniencySettings: nextStep.showLeniencySettings,
                    showTypingModeToggle: nextStep.showTypingModeToggle,
                  },
                  challengesCompleted: this.state.challengesCompleted + 1,
                  challengesCorrect:
                    this.state.challengesCorrect +
                    (nextStep.correct === "yes" ? 1 : 0),
                  checkEnabled: false,
                });
                break;

              default:
                throw Error("Step not recognized");
            }
          }}
          reportRecallEase={async (recallEase: number, difficulty: number) => {
            // Send feedback to server
            const userWord = stores.practiceStore.get(card.wordID);

            await stores.practiceStore.reportRecallEase(
              userWord,
              recallEase,
              difficulty,
              this.practiceType() === "blitz" ? "blitz" : "mastery"
            );
            walkthrough.setAchievement("flashcards", true);

            analytics.trackEventWithProperties("flashcardRecall", {
              "Practice Type": this.practiceType(),
            });
          }}
          allUserWords={stores.practiceStore}
          skipped={this.state.feedback?.type === "skipped"}
          persistedState={this.state.challengePersistedState}
          enableCheck={(checkEnabled) => this.setState({ checkEnabled })}
          selectedContextIndex={this.state.selectedContextIndex}
          setCardMode={(introCardMode) => {
            this.setState({ introCardMode });
          }}
          introCardMode={this.state.introCardMode}
          showingExplanation={() => {
            if (!this.state.challengeShowingExplanation) {
              this.setState({ challengeShowingExplanation: true });
            }
          }}
        />
      </div>
    );
  }

  render() {
    if (this.state.status === "loading") {
      return (
        <div class="practiceView">
          <div class="middle">
            <p>Loading...</p>
          </div>
        </div>
      );
    }

    const currentCard = this.state.queue[0];
    const userWord = stores.practiceStore.get(currentCard.wordID);
    const wordOrPhrase = userWord.isPhrase() ? "phrase" : "word";

    const renderSkipTypingToggle = () => (
      <p>
        <label>
          <input
            type="checkbox"
            checked={false}
            onChange={(event) => {
              userWord.save(
                {
                  passiveMode: event.currentTarget.checked,
                },
                { patch: true }
              );
            }}
          ></input>{" "}
          Skip typing for "{userWord.state.value.word}"
        </label>
      </p>
    );

    return (
      <div class="practiceView">
        <SessionHeaderView
          onClose={() => {
            if (this.state.editingCard) {
              this.stopEditingCard();
              return;
            }
            analytics.trackEventWithProperties("quitFlashcards", {
              "Practice Type": this.practiceType(),
            });
            this.props.appRouter.navigate("/learn?newPractice=true", {
              trigger: true,
            });
          }}
          progress={this.progress()}
        >
          <DropdownMenu
            // Using the message in the key as a hack to force the component to re-render when
            // a word is favorited.
            // (The need to do this kind of thing highlights the shortcomings of using
            // imperative stores in preact's React-like architecture)
            key={`${userWord.id}-${this.state.message}`}
            items={[
              {
                type: "toggle",
                label: "Auto-Pronounce",
                initialValue: Boolean(stores.user.state.peek().speakingMode),
                disabled:
                  tts.getAvailabilitySignal({
                    user: stores.user,
                    feature: "speakingMode",
                    language: userWord.state.value.language,
                  }).value === "unsupported",
                onDisabledClick: () => {
                  const language = userWord.state.value.language;
                  const languageName =
                    languageMap.map[language]?.english ?? language;
                  alert(
                    `No TTS voices are currently available for ${languageName}`
                  );
                },
                onToggle: (value) => {
                  speakingModeControls.setSpeakingModeWithVoicePrompt(
                    value,
                    userWord.state.value.language
                  );
                },
              },
              ...ttsMenu.buildVoiceMenuItems({
                language: userWord.state.value.language,
                feature: "speakingMode",
                onChange: () => this.forceUpdate(),
              }),
              {
                type: "toggle",
                label: "Sound effects",
                initialValue: Boolean(
                  stores.user.state.peek().soundEffectsEnabled
                ),
                onToggle: (value) => {
                  stores.user.save({ soundEffectsEnabled: value });
                },
              },
              {
                type: "separator",
              },
              {
                type: "toggle",
                label: `Favorite ${wordOrPhrase}`,
                initialValue: userWord.state.value.favorite,
                onToggle: (value) => {
                  userWord.save({ favorite: value }, { patch: true });
                },
              },
              {
                type: "action",
                label: `Edit ${wordOrPhrase}`,
                onClick: () => {
                  editCardDialog.show(userWord, {
                    title: "Edit Card",
                    doneCallback: () => {
                      this.stopEditingCard();
                      this.forceUpdate();
                    },
                  });
                },
              },
              {
                type: "action",
                label: `Delete ${wordOrPhrase}`,
                destructive: true,
                onClick: () => this.deleteCard(),
              },
            ]}
          />
        </SessionHeaderView>
        {this.state.editingCard ? (
          <>
            <div
              class="scrollableArea"
              ref={(element) => {
                this.scrollableArea = element;
              }}
            >
              <div class="topSpacer noMobile"></div>
              <div class="middle">
                <div class="centeredContent">
                  <PracticeEditView
                    ref={(ref) => {
                      this.editView = ref;
                    }}
                    userWord={stores.practiceStore.get(currentCard.wordID)}
                    onEdit={() => {
                      this.setState({ edited: true });
                    }}
                    selectedContextIndex={this.state.selectedContextIndex}
                    selectContextIndex={(index) =>
                      this.setState({ selectedContextIndex: index })
                    }
                  />
                </div>
              </div>
              <div class="bottomSpacer"></div>
              {this.state.message ? this.renderMessage() : null}
            </div>

            <div class="footer">
              <div class="footerButtons">
                <button onClick={() => this.deleteCard()}>DELETE</button>
                {this.state.edited ? (
                  <button
                    onClick={() => {
                      this.editView.undo();
                      this.setState({ edited: false });
                    }}
                  >
                    UNDO
                  </button>
                ) : null}
                <button
                  class="continueButton"
                  onClick={() => {
                    if (this.state.edited) {
                      this.editView.save();
                    }
                    this.stopEditingCard();
                  }}
                >
                  {this.state.edited ? "Save" : "Continue"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              class="scrollableArea"
              ref={(element) => {
                this.scrollableArea = element;
              }}
            >
              <div class="topSpacer noMobile"></div>
              <div class="middle challenge">
                <div class="centeredContent">
                  {this.renderChallenge(currentCard)}
                  {userWord.state.value.note ? (
                    this.state.feedback ||
                    this.state.showingNote ||
                    currentCard.stage === "intro" ? (
                      <p>
                        <h4>NOTE</h4>
                        <span class="preserveLineBreaks">
                          {userWord.state.value.note}
                        </span>
                      </p>
                    ) : (
                      <p>
                        <button
                          onClick={() => {
                            this.setState({ showingNote: true });
                          }}
                        >
                          Show Note
                        </button>
                      </p>
                    )
                  ) : null}
                </div>
              </div>
              <div class="bottomSpacer"></div>
              {this.renderMessage()}
            </div>
            <div class="footer">
              {(() => {
                if (this.state.showLeniencySettings) {
                  return (
                    <div class="feedbackRibbon neutral">
                      <div class="feedbackContent">
                        <h3>Typing settings</h3>
                        <form class="leniencySettings">
                          <ul>
                            <li>
                              <label>
                                <input
                                  type="radio"
                                  name="leniency"
                                  checked={
                                    practiceUtils.getLeniency() === "allowTypos"
                                  }
                                  onClick={() => {
                                    practiceUtils.setLeniency("allowTypos");
                                  }}
                                ></input>{" "}
                                Allow typos and missing accents
                              </label>
                            </li>
                            <li>
                              <label>
                                <input
                                  type="radio"
                                  name="leniency"
                                  checked={
                                    practiceUtils.getLeniency() ===
                                    "allowMissingAccents"
                                  }
                                  onClick={() => {
                                    practiceUtils.setLeniency(
                                      "allowMissingAccents"
                                    );
                                  }}
                                ></input>{" "}
                                Allow missing accents
                              </label>
                            </li>
                            <li>
                              <label>
                                <input
                                  type="radio"
                                  name="leniency"
                                  checked={
                                    practiceUtils.getLeniency() === "strict"
                                  }
                                  onClick={() => {
                                    practiceUtils.setLeniency("strict");
                                  }}
                                ></input>{" "}
                                Strict mode. No mistakes allowed!
                              </label>
                            </li>
                            <li>{renderSkipTypingToggle()}</li>
                          </ul>
                        </form>
                      </div>
                    </div>
                  );
                }

                if (
                  this.state.feedback === undefined ||
                  currentCard.stage === "intro"
                ) {
                  return null;
                }

                switch (this.state.feedback?.type) {
                  case "correct":
                    return (
                      <div class="feedbackRibbon correct">
                        <SoundEffectView soundEffect="correct" />
                        <div class="feedbackContent">
                          {this.state.feedback.showLeniencySettings
                            ? this.renderLeniencySettingsButton()
                            : null}
                          {this.state.feedback.message ? (
                            <>
                              <h3>{this.state.feedback.message.title}</h3>
                              {this.state.feedback.message.body ? (
                                <p>{this.state.feedback.message.body}</p>
                              ) : null}
                            </>
                          ) : (
                            <h3>Correct!</h3>
                          )}
                        </div>
                      </div>
                    );

                  case "almost":
                    return (
                      <div class="feedbackRibbon neutral">
                        <SoundEffectView soundEffect="incorrect" />
                        <div class="feedbackContent">
                          {this.state.feedback.showLeniencySettings
                            ? this.renderLeniencySettingsButton()
                            : null}
                          {this.state.feedback.message ? (
                            <>
                              <h3>{this.state.feedback.message.title}</h3>
                              {this.state.feedback.message.body ? (
                                <p>{this.state.feedback.message.body}</p>
                              ) : null}
                            </>
                          ) : (
                            <h3>Almost!</h3>
                          )}
                        </div>
                      </div>
                    );

                  case "incorrect":
                    return (
                      <div
                        class={`feedbackRibbon ${this.state.feedback.message.title === "Skipped" ? "neutral" : "incorrect"}`}
                      >
                        <SoundEffectView soundEffect="incorrect" />
                        <div class="feedbackContent">
                          {this.state.feedback.showLeniencySettings
                            ? this.renderLeniencySettingsButton()
                            : null}
                          {this.state.feedback.message ? (
                            <>
                              <h3>{this.state.feedback.message.title}</h3>
                              {this.state.feedback.message.body ? (
                                <p>{this.state.feedback.message.body}</p>
                              ) : null}
                            </>
                          ) : (
                            <h3>Incorrect!</h3>
                          )}
                          {this.state.feedback.showTypingModeToggle
                            ? renderSkipTypingToggle()
                            : null}
                        </div>
                      </div>
                    );

                  case "deleted":
                  case "skipped":
                    console.assert(
                      this.state.feedback.message !== undefined,
                      "Neutral feedback state requires a message"
                    );
                    return (
                      <div class="feedbackRibbon neutral">
                        <div class="feedbackContent">
                          {this.state.feedback.showLeniencySettings
                            ? this.renderLeniencySettingsButton()
                            : null}
                          <h3>{this.state.feedback.message.title}</h3>
                          {this.state.feedback.message.body ? (
                            <p>{this.state.feedback.message.body}</p>
                          ) : null}
                        </div>
                      </div>
                    );
                }
              })()}
              <div class="footerButtons">
                <button
                  onClick={() => {
                    const challengePersistedState =
                      this.challengeView?.getPersistedState?.();
                    this.setState({
                      editingCard: true,
                      challengePersistedState,
                    });
                  }}
                  disabled={
                    this.state.status === "challengeComplete" &&
                    this.state.feedback?.type === "deleted"
                  }
                >
                  {this.state.challengeShowingExplanation
                    ? "Edit"
                    : "Explain or edit"}
                </button>
                {this.state.checkEnabled ? (
                  <button
                    key={this.state.status + currentCard.wordID}
                    class="continueButton"
                    onClick={() => this.challengeView.checkAnswer?.()}
                  >
                    Check
                  </button>
                ) : (
                  <button
                    key={this.state.status + currentCard.wordID}
                    class="continueButton"
                    onClick={() => this.nextCard()}
                    autoFocus={true}
                    disabled={this.state.status !== "challengeComplete"}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  async deleteCard() {
    if (!confirm("Are you sure you want to delete this card?")) {
      return;
    }

    await this.setState({
      editingCard: false,
      status: "challengeComplete",
      feedback: {
        type: "deleted",
        message: { title: "Card removed" },
      },
      checkEnabled: false,
    });

    // Automatically skip to the next card
    this.nextCard();
  }

  renderMessage() {
    return (
      <div class={`practicePageMessage${this.state.message ? " show" : ""}`}>
        {this.state.message ?? ""}
      </div>
    );
  }

  renderLeniencySettingsButton() {
    return (
      <button
        class="leniencySettingsButton"
        onClick={() => {
          this.setState({ showLeniencySettings: true });
        }}
      >
        <i class="icon icon-cog"></i>
      </button>
    );
  }

  stopEditingCard() {
    const currentCard = this.state.queue[0];
    const priorFeedback = this.state.feedback;
    const hadAnswered =
      priorFeedback?.type === "correct" ||
      priorFeedback?.type === "incorrect" ||
      priorFeedback?.type === "almost";

    const feedback: State["feedback"] = hadAnswered
      ? this.state.edited
        ? { ...priorFeedback, message: { title: "Card updated" } }
        : priorFeedback
      : {
          type: "skipped",
          message:
            currentCard.stage === "intro"
              ? undefined
              : this.state.edited
                ? { title: "Card updated" }
                : { title: "Answer shown" },
          showLeniencySettings: currentCard.stage === "active",
        };

    this.setState({
      editingCard: false,
      feedback,
      status: "challengeComplete",
    });
  }

  practiceType() {
    return this.from === "chat"
      ? "new"
      : stores.user.state.value.practiceType ?? "blitz";
  }

  async nextCard() {
    const currentCard = this.state.queue[0];
    const userWord = stores.practiceStore.get(currentCard.wordID);

    let queue = this.state.queue;
    let completed = this.state.completed;

    const currentCardPassiveMode =
      userWord.state.value.passiveMode || this.practiceType() === "blitz";

    if (currentCardPassiveMode && currentCard.stage === "active") {
      // The user must have just disabled typing for this card, so put it back into passive mode
      currentCard.stage = "passive";
    }

    if (
      this.state.feedback?.type === "deleted" ||
      (currentCard.stage === "intro" && this.state.introCardMode === "delete")
    ) {
      // Remove this card from the queue
      queue = queue.slice(1);

      // Destroy the card
      userWord.deleteUserWord();
    } else if (
      this.state.feedback?.type === "correct" &&
      (this.from === "chat" || Number(userWord.state.value.interval) > 0) &&
      (currentCard.stage === "active" ||
        (currentCard.stage === "passive" && currentCardPassiveMode))
    ) {
      // Done with this card, remove it from queue and add to completed
      queue = queue.slice(1);
      completed = [...completed, currentCard];
      this.logWordPracticed();
    } else {
      // Put this card back in the queue, upgrading its state if the user got it correct
      if (currentCard.stage === "intro") {
        if (this.practiceType() !== "blitz") {
          switch (this.state.introCardMode) {
            case "active":
              userWord.save({ passiveMode: false }, { patch: true });
              break;

            case "passive":
              userWord.save({ passiveMode: true }, { patch: true });
              break;
          }
        }

        currentCard.stage = "passive";
      }

      if (
        currentCard.stage === "passive" &&
        this.state.feedback?.type === "correct" &&
        !currentCardPassiveMode
      ) {
        currentCard.stage = "active";
      }

      /** The ideal minimum number of cards to wait before resurfacing this card */
      const idealDelayMinimum = 4;

      // Insert at a random position at least `idealDelayMinimum` cards away from the start
      const insertIndex =
        queue.length <= idealDelayMinimum
          ? queue.length
          : _.random(idealDelayMinimum, queue.length);
      queue = [
        ...queue.slice(1, insertIndex),
        currentCard,
        ...queue.slice(insertIndex),
      ];
    }

    if (queue.length === 0) {
      // Check if this was a perfect session (no mistakes)
      const isPerfectSession =
        this.state.challengesCompleted > 0 &&
        this.state.challengesCorrect === this.state.challengesCompleted;

      const perfectParam = isPerfectSession ? "&perfectSession=true" : "";

      this.props.appRouter.navigate(
        `/learn?sessionComplete=true&newPractice=true${perfectParam}`,
        {
          trigger: true,
        }
      );
      return;
    }

    await this.setState({
      status: "challengeActive",
      queue,
      completed,
      feedback: undefined,
      challengeCount: this.state.challengeCount + 1,
      editingCard: false,
      edited: false,
      checkEnabled: false,
      showLeniencySettings: false,
      selectedContextIndex:
        queue.length > 0
          ? this.pickContextIndex(stores.practiceStore.get(queue[0].wordID))
          : undefined,
      introCardMode: this.getIntroCardMode(queue[0]?.wordID),
      challengeShowingExplanation: false,
      showingNote: false,
      challengePersistedState: undefined,
    });

    // Scroll to top of scrollable area when showing the next card
    if (this.scrollableArea) {
      this.scrollableArea.scrollTop = 0;
    }
  }

  /**
   * This is non-deterministic. If user has specified a bookID it will prefer contexts from that
   * book.
   *
   * (Ideally we'd also pick a context which has an explanation, but since that involves a server
   * request we'll just pick the first one for now.)
   */
  pickContextIndex(userWord: UserWordStore) {
    let contexts = userWord.state.value.contexts ?? [];

    if (this.from === "chat") {
      // Always pick the most recent (last) context
      return contexts.length - 1;
    }

    if (this.props.options.bookID) {
      const filteredContextIndices = contexts
        .map((context, index) => ({ context, index }))
        .filter(
          ({ context }) => context.bookID?._id === this.props.options.bookID
        );

      if (filteredContextIndices.length > 0) {
        return _.sample(filteredContextIndices).index;
      }
    }

    return _.sample(_.range(contexts.length));
  }

  logWordPracticed() {
    const language = stores.user.currentLearningLanguage.value;
    void stores.streak.logPracticed(1, language);
    if (language) stores.user.markLanguageActive(language);
  }

  componentDidMount() {
    // Re-render when the server-side Azure voice pref changes, since picking
    // an Azure voice in the picker updates user.ttsVoices rather than
    // localStorage.
    this.disposeUserEffect = effect(() => {
      void stores.user.state.value.ttsVoices;
      this.forceUpdate();
    });
    this.disposeTtsAvailabilityEffect = effect(() => {
      const language = stores.user.currentLearningLanguage.value;
      void tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language,
      }).value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeUserEffect?.();
    this.disposeTtsAvailabilityEffect?.();
    keyboardShortcuts.unregisterHandler(
      "practicePage",
      keyboardShortcuts.keyCodes.enter
    );
    keyboardShortcuts.unregisterHandler(
      "practicePage",
      "E".charCodeAt(0),
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
    keyboardShortcuts.unregisterHandler(
      "practicePage",
      "D".charCodeAt(0),
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
    keyboardShortcuts.unregisterHandler(
      "practicePage",
      "F".charCodeAt(0),
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
  }
}

export default PracticePageView;
